import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Save, Server, ShieldCheck, Trash2, Zap } from 'lucide-react';

/**
 * Postfachverwaltung für den Mailversand — nur für Administratoren.
 *
 * Anlass (17.09.2026): Der Mailserver wies alle drei Zugänge gleichzeitig ab.
 * Die Kennwörter lagen ausschließlich in Vercel-Umgebungsvariablen, eine
 * Korrektur hätte also einen Deploy gebraucht — der Versand stand still.
 * Hier lassen sie sich ohne Deploy pflegen und vor dem Ernstfall prüfen.
 *
 * Passwörter werden nur gesendet, nie empfangen: Der Server meldet lediglich,
 * ob eines hinterlegt ist.
 */
export default function MailKontenPage({ apiBase = '/api', session = null }) {
  const accessToken = session?.access_token || null;
  const kopfzeilen = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const url = `${apiBase}/automation/mail-konten`;

  const [laden, setLaden] = useState(true);
  const [stand, setStand] = useState(null);
  const [fehler, setFehler] = useState('');
  const [arbeitet, setArbeitet] = useState('');
  const [entwuerfe, setEntwuerfe] = useState({});
  const [smtp, setSmtp] = useState({ host: '', port: '', secure: '' });

  const laden_ = useCallback(async () => {
    if (!accessToken) { setLaden(false); setFehler('Bitte anmelden.'); return; }
    setLaden(true);
    try {
      const antwort = await axios.get(url, { headers: kopfzeilen });
      setStand(antwort.data);
      setSmtp({
        host: antwort.data?.smtp?.host || '',
        port: antwort.data?.smtp?.port || '',
        secure: antwort.data?.smtp?.secure || '',
      });
      setFehler('');
    } catch (f) {
      setFehler(f?.response?.data?.detail || f?.message || 'Postfächer konnten nicht geladen werden.');
    } finally {
      setLaden(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, url]);

  useEffect(() => { laden_(); }, [laden_]);

  const senden = useCallback(async (nutzlast, kennung) => {
    setArbeitet(kennung);
    try {
      const antwort = await axios.post(url, nutzlast, { headers: kopfzeilen });
      if (antwort.data?.konten) setStand(antwort.data);
      if (antwort.data?.message) {
        if (antwort.data.ok === false) toast.error(antwort.data.message);
        else toast.success(antwort.data.message);
      }
      return antwort.data;
    } catch (f) {
      toast.error(f?.response?.data?.detail || f?.message || 'Aktion fehlgeschlagen.');
      return null;
    } finally {
      setArbeitet('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const entwurf = (email) => entwuerfe[email] || {};
  const setzeEntwurf = (email, feld, wert) => setEntwuerfe((v) => ({
    ...v, [email]: { ...(v[email] || {}), [feld]: wert },
  }));

  if (laden) return <div style={{ padding: 24, color: '#64748B' }}>Postfächer werden geladen …</div>;

  if (fehler) {
    return (
      <div style={{ padding: 24 }}>
        <div style={kasten('#FEE2E2', '#FECACA', '#991B1B')}>{fehler}</div>
      </div>
    );
  }

  const konten = stand?.konten || [];

  return (
    <div style={{ padding: '20px 24px', display: 'grid', gap: 18, maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Mail size={20} color="#122A5C" />
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: '#122A5C', fontFamily: 'Outfit, sans-serif' }}>
            Postfächer für den Mailversand
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748B' }}>
            Zugangsdaten pflegen, ohne dass ein Deploy nötig ist. Passwörter werden verschlüsselt
            gespeichert und nie wieder angezeigt.
          </p>
        </div>
      </div>

      {!stand?.eigenerSchluessel ? (
        <div style={kasten('#FEF3C7', '#FDE68A', '#92400E')}>
          <strong>Hinweis zum Schlüssel.</strong> Die Passwörter sind mit einem Schlüssel verschlüsselt,
          der vom Supabase-Service-Key abgeleitet ist. Wird dieser gewechselt, sind alle hinterlegten
          Passwörter unlesbar und müssen neu eingetragen werden. Ein eigener Schlüssel
          (<code>MAIL_KONTEN_SECRET_KEY</code>) macht sie davon unabhängig.
        </div>
      ) : null}

      {/* Serverdaten */}
      <section style={karte}>
        <div style={kopf}><Server size={14} color="#475569" /><span>Mailserver</span></div>
        <div style={{ padding: 14, display: 'grid', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>
            Gilt für alle Postfächer. Leer lassen, um die Werte aus den Umgebungsvariablen zu verwenden.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Feld titel="Host" wert={smtp.host} setzen={(w) => setSmtp((s) => ({ ...s, host: w }))} platzhalter="mail.agenturserver.de" breite={260} />
            <Feld titel="Port" wert={smtp.port} setzen={(w) => setSmtp((s) => ({ ...s, port: w }))} platzhalter="465" breite={90} />
            <Feld titel="TLS (true/false)" wert={smtp.secure} setzen={(w) => setSmtp((s) => ({ ...s, secure: w }))} platzhalter="true" breite={130} />
          </div>
          <button
            type="button"
            onClick={() => senden({ aktion: 'smtp', ...smtp }, 'smtp')}
            disabled={arbeitet === 'smtp'}
            style={knopf('#122A5C', '#FFFFFF')}
          >
            <Save size={12} /> {arbeitet === 'smtp' ? '…' : 'Servereinstellungen speichern'}
          </button>
        </div>
      </section>

      {/* Bestehende Postfächer */}
      <section style={karte}>
        <div style={kopf}>
          <ShieldCheck size={14} color="#475569" />
          <span>Postfächer ({konten.length})</span>
        </div>

        {konten.length === 0 ? (
          <div style={{ padding: 14, color: '#64748B', fontSize: 12.5, lineHeight: 1.55 }}>
            Es ist kein Absender freigegeben. Das deutet auf einen Fehler beim Laden hin.
          </div>
        ) : konten.map((konto) => {
          const e = entwurf(konto.email);
          return (
            <div key={konto.email} style={{ padding: 14, borderTop: '1px solid #F1F5F9', display: 'grid', gap: 9 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13.5, color: '#0F172A' }}>{konto.email}</strong>
                <span style={abzeichen(zustand(konto))}>{zustand(konto).text}</span>
                {konto.freigegeben === false ? (
                  <span style={abzeichen({ ton: 'warnung' })} title="getSmtpConfig weist diese Adresse beim Versand ab">
                    nicht versandfähig
                  </span>
                ) : null}
                {konto.aktualisiertAm ? (
                  <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#94A3B8' }}>
                    zuletzt {new Date(konto.aktualisiertAm).toLocaleString('de-DE')}
                    {konto.aktualisiertVon ? ` · ${konto.aktualisiertVon}` : ''}
                  </span>
                ) : null}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Feld titel="Anzeigename" wert={e.label ?? konto.label} setzen={(w) => setzeEntwurf(konto.email, 'label', w)} breite={200} />
                <Feld titel="SMTP-Benutzer" wert={e.benutzer ?? konto.benutzer} setzen={(w) => setzeEntwurf(konto.email, 'benutzer', w)} platzhalter="z. B. p708865p7" breite={240} />
                <Feld titel="Neues Passwort" typ="password" wert={e.passwort || ''} setzen={(w) => setzeEntwurf(konto.email, 'passwort', w)} platzhalter="leer lassen = unverändert" breite={220} />
              </div>

              <p style={{ margin: 0, fontSize: 11, color: '#94A3B8', lineHeight: 1.5 }}>
                Der <strong>SMTP-Benutzer ist beim Anbieter meist nicht die Mailadresse</strong>, sondern
                die Postfachkennung (etwa <code>p708865p7</code>). Mit der Adresse als Benutzer antwortet
                der Server <code>535 5.7.8</code> — obwohl das Passwort stimmt und der Abruf per IMAP
                damit funktioniert. Die Kennung steht beim Anbieter bei den Zugangsdaten des Postfachs.
              </p>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={async () => {
                    await senden({ email: konto.email, label: e.label ?? konto.label, benutzer: e.benutzer ?? konto.benutzer, passwort: e.passwort || '' }, `save:${konto.email}`);
                    setzeEntwurf(konto.email, 'passwort', '');
                  }}
                  disabled={arbeitet === `save:${konto.email}`}
                  style={knopf('#122A5C', '#FFFFFF')}
                >
                  <Save size={12} /> {arbeitet === `save:${konto.email}` ? '…' : 'Speichern'}
                </button>

                <button
                  type="button"
                  onClick={() => senden({ aktion: 'pruefen', email: konto.email, passwort: e.passwort || '', ...smtp }, `test:${konto.email}`)}
                  disabled={arbeitet === `test:${konto.email}`}
                  title="Meldet sich am Mailserver an, ohne eine Mail zu senden - und ohne etwas zu speichern"
                  style={knopf('#FFFFFF', '#122A5C', '#CBD5E1')}
                >
                  <Zap size={12} /> {arbeitet === `test:${konto.email}` ? '…' : 'Anmeldung testen'}
                </button>

                {konto.hatPasswort ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm(`Den hier gespeicherten Zugang für ${konto.email} entfernen?

`
                        + 'Der Versand fällt danach auf die Umgebungsvariablen zurück.')) return;
                      senden({ aktion: 'loeschen', email: konto.email }, `del:${konto.email}`);
                    }}
                    disabled={arbeitet === `del:${konto.email}`}
                    title="Entfernt nur den hier gespeicherten Zugang, nicht das Postfach beim Anbieter"
                    style={{ ...knopf('#FFFFFF', '#991B1B', '#FECACA'), marginLeft: 'auto' }}
                  >
                    <Trash2 size={12} /> Gespeicherten Zugang entfernen
                  </button>
                ) : null}
              </div>

              {e.passwort ? (
                <div style={{ ...kasten('#FEF3C7', '#FDE68A', '#92400E'), padding: '7px 10px', fontSize: 11.5 }}>
                  Das eingetippte Passwort ist <strong>noch nicht gespeichert</strong>. „Anmeldung testen"
                  prüft es nur; erst „Speichern" hinterlegt es dauerhaft.
                </div>
              ) : null}
            </div>
          );
        })}
      </section>

      <p style={{ margin: 0, fontSize: 11.5, color: '#94A3B8', lineHeight: 1.6 }}>
        Die Liste der Absender steht im Quelltext (<code>ALLOWED_SENDERS</code> in
        <code> api/_lib/mailSender.js</code>) und lässt sich hier nicht erweitern: Eine Adresse,
        die dort fehlt, weist der Versand ohnehin ab. Ein weiteres Postfach ist deshalb eine
        Code-Änderung, keine Einstellung.
      </p>
    </div>
  );
}

const karte = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' };
const kopf = {
  padding: '11px 14px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC',
  display: 'flex', alignItems: 'center', gap: 7, color: '#475569', fontSize: 12, fontWeight: 700,
};

const TOENE = {
  gut: { hintergrund: '#DCFCE7', farbe: '#166534' },
  neutral: { hintergrund: '#E0F2FE', farbe: '#075985' },
  warnung: { hintergrund: '#FEF3C7', farbe: '#92400E' },
  fehlt: { hintergrund: '#FEE2E2', farbe: '#991B1B' },
};

/**
 * Woher der Zugang dieses Postfachs kommt. Die Unterscheidung ist der Kern der
 * Seite: "aus Umgebungsvariablen" heisst, dass eine Korrektur einen Deploy
 * braeuchte - genau das soll hier nicht mehr noetig sein.
 */
function zustand(konto) {
  // Gespeichert, aber nicht entschlüsselbar: Das passiert, wenn der Schlüssel
  // gewechselt hat. Der Versand nimmt dann klammheimlich wieder die
  // Umgebungsvariablen - das muss man sehen können.
  if (konto.hatPasswort && konto.lesbar === false) {
    return { ton: 'warnung', text: 'gespeichert, aber nicht lesbar' };
  }
  if (konto.hatPasswort) return { ton: 'gut', text: 'Passwort hier hinterlegt' };
  if (konto.ausUmgebung) return { ton: 'neutral', text: 'nutzt Umgebungsvariablen' };
  return { ton: 'fehlt', text: 'kein Passwort' };
}

function abzeichen({ ton = 'neutral' }) {
  const farben = TOENE[ton] || TOENE.neutral;
  return {
    background: farben.hintergrund, color: farben.farbe,
    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
  };
}

function kasten(hintergrund, rand, farbe) {
  return {
    background: hintergrund, border: `1px solid ${rand}`, color: farbe,
    padding: '10px 12px', borderRadius: 9, fontSize: 12, lineHeight: 1.55,
  };
}

function knopf(hintergrund, farbe, rand = hintergrund) {
  return {
    padding: '7px 12px', border: `1px solid ${rand}`, borderRadius: 7,
    background: hintergrund, color: farbe, fontSize: 12, fontWeight: 700,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontFamily: 'inherit',
  };
}

function Feld({ titel, wert, setzen, platzhalter = '', typ = 'text', breite = 200 }) {
  return (
    <label style={{ display: 'grid', gap: 3 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {titel}
      </span>
      <input
        type={typ}
        value={wert || ''}
        onChange={(ereignis) => setzen(ereignis.target.value)}
        placeholder={platzhalter}
        autoComplete={typ === 'password' ? 'new-password' : 'off'}
        style={{
          width: breite, padding: '7px 9px', border: '1px solid #CBD5E1',
          borderRadius: 7, fontSize: 12.5, fontFamily: 'inherit',
        }}
      />
    </label>
  );
}
